import {
  useRichTextEditor,
  type RteControlComponent,
  type RteControlSpec,
} from '@shared/rich-text-editor-core';
import type { ChainedCommands } from '@tiptap/core';
import type { Editor } from '@tiptap/react';
import React, { useState, useEffect } from 'react';

import ControlButton, { executeOperation } from './control-button';

/**
 * Factory for simple toolbar controls that toggle/execute a TipTap command.
 *
 * Usage:
 * const BoldControl = makeSimpleControl(
 *   'Bold',
 *   BoldIcon,
 *   { name: 'toggleBold' },
 *   { name: 'bold' }
 * );
 */
const makeSimpleControl = <TOperation extends keyof ChainedCommands>(
  _label: string,
  Icon: React.FC,
  op: RteControlSpec<TOperation>['operation'],
  active?: { name: string; attributes?: Record<string, unknown> },
  disabled?: (editor: Editor) => boolean,
): RteControlComponent => {
  return function Control() {
    const { editor } = useRichTextEditor();
    const [, setTick] = useState(0);

    useEffect(() => {
      if (!editor) return;

      const rerender = () => setTick((t) => t + 1);

      editor.on('selectionUpdate', rerender);
      editor.on('transaction', rerender);
      editor.on('update', rerender);
      editor.on('focus', rerender);
      editor.on('blur', rerender);

      return () => {
        editor.off('selectionUpdate', rerender);
        editor.off('transaction', rerender);
        editor.off('update', rerender);
        editor.off('focus', rerender);
        editor.off('blur', rerender);
      };
    }, [editor]);

    if (!editor) return null;

    const isActive = active ? editor.isActive(active.name, active.attributes) : false;
    const isDisabled = disabled ? disabled(editor) : false;

    return (
      <ControlButton
        active={isActive}
        disabled={isDisabled}
        onClick={() => executeOperation(editor, op)}
      >
        <Icon />
      </ControlButton>
    );
  };
};

export default makeSimpleControl;
export { makeSimpleControl };
